import React, { useRef } from 'react';
import ReactToPrint from 'react-to-print';
// @mui
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import TextField from '@mui/material/TextField';
import { Box, GlobalStyles, Typography } from '@mui/material';
import Iconify from 'src/components/iconify';
// types
import { ForeignBeneficiary, LocalBeneficiary, Transfer } from 'src/redux/slices/transfersSlice';
import { numberToFrenchText } from 'src/utils/format/number-to-test';
import { getBankById } from 'src/utils/accounts/banks';

interface Props {
    open: boolean;
    onClose: () => void;
    row: Transfer;
}

export default function PrintTransfer({ open, onClose, row }: Props) {
    console.log(row);
    const previewRef = useRef<HTMLDivElement>(null);

    return (
        <>
            <GlobalStyles
                styles={{
                    '@media print': {
                        '@page': {
                            size: 'A1 landscape',
                            margin: 0,
                        },
                        body: {
                            margin: 0,
                            padding: 0,
                            lineHeight: 1.5,
                        },
                    },
                }}
            />

            <Dialog open={open} disableEscapeKeyDown onClose={(_, reason) => reason !== 'backdropClick' && onClose()} maxWidth="lg" fullWidth>
                <DialogTitle>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <Iconify icon="eva:printer-fill" sx={{ fontSize: 40, mr: 1 }} />
                        Print Transfer
                    </div>
                </DialogTitle>

                <DialogContent>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px', padding: '16px' }}>
                        {/* Agency */}
                        <TextField
                            label="الوكالة"
                            defaultValue={row.company?.name || ''}
                            fullWidth
                            variant="outlined"
                        />

                        {/* Date */}
                        <TextField
                            label="التاريخ"
                            defaultValue={row.date_d_emition}
                            fullWidth
                            variant="outlined"
                        />

                        {/* Account Owner */}
                        <TextField
                            label="مالك الحساب"
                            defaultValue={row.beneficiere?.name || ''}
                            fullWidth
                            variant="outlined"
                        />

                        {/* Account Number */}
                        <TextField
                            label="رقم الحساب"
                            defaultValue={row.beneficiere?.name || ''}
                            fullWidth
                            variant="outlined"
                        />

                        {/* Amount */}
                        <TextField
                            label="المبلغ"
                            defaultValue={row.amount}
                            fullWidth
                            variant="outlined"
                        />

                        {/* Beneficiary Name */}
                        <TextField
                            label="اسم المستفيد"
                            defaultValue={row.beneficiere?.name || ''}
                            fullWidth
                            variant="outlined"
                        />

                        {/* Beneficiary Account */}
                        <TextField
                            label="حساب المستفيد"
                            defaultValue={
                                row.beneficiere_type === 'local'
                                    ? (row.beneficiere as LocalBeneficiary)?.rib || ''
                                    : (row.beneficiere as ForeignBeneficiary)?.iban || ''
                            }
                            fullWidth
                            variant="outlined"
                            inputProps={{ maxLength: 24 }}
                        />

                        {/* Beneficiary Bank */}
                        <TextField
                            label="بنك المستفيد"
                            defaultValue={row.bank_id?.toString() || ''}
                            fullWidth
                            variant="outlined"
                        />
                    </div>

                    {/* Preview Section */}
                    <div ref={previewRef} style={{
                        display: 'flex',
                        justifyContent: 'center'
                    }}>
                        <OrdreDeVirement
                            data={row}
                        />
                    </div> 
                </DialogContent>

                <DialogActions>
                    <ReactToPrint
                        trigger={() => <Button variant="contained">Print</Button>}
                        content={() => previewRef.current}
                    />
                    <Button variant="outlined" onClick={onClose}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

interface OrdreDeVirementProps {
    data: Transfer
}

const OrdreDeVirement: React.FC<OrdreDeVirementProps> = ({ data }) => {
    const formattedDate = new Date(data.date_d_emition).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
    });
    const formattedAmount = data.amount.toLocaleString('fr-MA', {
        style: 'currency',
        currency: 'MAD',
    });
    const amountInWords = numberToFrenchText(data.amount).toUpperCase();
    const bankName = getBankById((data.beneficiere as LocalBeneficiary)?.bank_id)?.name;

    return (
        <div
            style={{
                width: '800px',
                height: 'auto',
                border: '1px solid #000',
                borderRadius: '15px',
                borderBottom: 'none',
                padding: '16px',
                fontFamily: 'Arial, sans-serif',
                textAlign: 'left',
            }}
        >
            {/* Header */}
            <Typography variant="h5" align="center" sx={{ fontWeight: 'bold', marginTop: '20px', marginBottom: '80px' }}>
                Ordre de Virement N° {data.numero}
            </Typography>

            {/* Agency and Date */}
            <Typography variant="body1" sx={{ marginBottom: '16px' }}>
                <strong>Objet:</strong> Ordre de virement
            </Typography>
            <Typography variant="body1" sx={{ marginBottom: '16px' }}>
                <strong>Agence:</strong> {data.company?.name}
            </Typography>
            <Typography variant="body1" sx={{ marginBottom: '80px' }}>
                <strong>Date:</strong> {formattedDate}
            </Typography>

            {/* Amount Table */}
            <Box sx={{ marginBottom: '16px' }}>
                <Typography variant="body1" sx={{ fontWeight: 'bold', marginBottom: '8px' }}>
                    Par le débit de votre compte n° [numéro de compte], nous vous prions de bien vouloir procéder au virement en faveur du bénéficiaire ci-après
                </Typography>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                        <tr>
                            <th style={{ border: '1px solid #000', padding: '8px' }}>#</th>
                            <th style={{ border: '1px solid #000', padding: '8px' }}>Bénéficiaire</th>
                            <th style={{ border: '1px solid #000', padding: '8px' }}>La banque</th>
                            <th style={{ border: '1px solid #000', padding: '8px' }}>RIB</th>
                            <th style={{ border: '1px solid #000', padding: '8px' }}>Montant</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td style={{ border: '1px solid #000', padding: '8px' }}>1</td>
                            <td style={{ border: '1px solid #000', padding: '8px' }}>{data.beneficiere?.name}</td>
                            <td style={{ border: '1px solid #000', padding: '8px' }}>{bankName}</td>
                            <td style={{ border: '1px solid #000', padding: '8px' }}>{(data.beneficiere as LocalBeneficiary)?.rib}</td>
                            <td style={{ border: '1px solid #000', padding: '8px' }}>{formattedAmount}</td>
                        </tr>
                    </tbody>
                </table>
            </Box>

            {/* Total Amount */}
            <Typography variant="body1" sx={{ marginBottom: '16px' }}>
                <strong>Total:</strong> {formattedAmount}
            </Typography>
            <Typography variant="body1" sx={{ fontStyle: 'bold' }}>
                #{amountInWords}#
            </Typography>

            {/* Footer */}
            <Typography variant="body1" sx={{ marginTop: '40px', textAlign: 'right' }}>
                Veuillez agréer, Messieurs, l&apos;expression de nos salutations les plus distinguées
            </Typography>
            <Typography variant="body1" sx={{ marginTop: '40px', marginBottom: '80px', textAlign: 'right' }}>
                Direction
            </Typography>
        </div>
    );
};
